import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Card, Flex, Progress, Typography } from "antd";
import { useEffect, useState } from "react";
import type { ServiceCount } from "../../generated-axios-client";
import { SERVICE_CHART_COLORS } from "../dashboard.constants";
import styles from "../Dashboard.module.css";

const PAGE_SIZE = 5;

type Props = {
  intl: IntlShape;
  entries: ServiceCount[] | undefined;
  total: number;
  loading: boolean;
  onOpenService: (serviceId: string) => void;
};

export const WorkOrdersByServiceChart = ({
  intl,
  entries = [],
  total,
  loading,
  onOpenService,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(0);

  /* biome-ignore lint/correctness/useExhaustiveDependencies: `entries` is a destructured
     prop and is genuinely reactive; paging must go back to the first page whenever the
     dataset changes, or the user is left on a page that no longer exists. */
  useEffect(() => {
    setCurrentPage(0);
  }, [entries]);

  const totalPages = Math.ceil(entries.length / PAGE_SIZE);
  const pageStart = currentPage * PAGE_SIZE;
  const pagedEntries = entries.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <Card
      className={styles.fullHeight}
      title={intl.formatMessage({ id: "dashboard_service_title" })}
      loading={loading}
    >
      {entries.length === 0 ? (
        <Typography.Text type="secondary">
          {intl.formatMessage({ id: "dashboard_no_data" })}
        </Typography.Text>
      ) : (
        <Flex vertical gap="middle">
          {pagedEntries.map(({ service_id, service_name, count }, index) => {
            const percent = total > 0 ? Math.round((count / total) * 100) : 0;
            const color =
              SERVICE_CHART_COLORS[(pageStart + index) % SERVICE_CHART_COLORS.length];
            const serviceName =
              service_name ?? intl.formatMessage({ id: "dashboard_no_service" });
            return (
              <Flex key={service_id ?? "none"} vertical gap="small">
                <Flex justify="space-between" gap="small">
                  {service_id ? (
                    <Typography.Link onClick={() => onOpenService(service_id)}>
                      {serviceName}
                    </Typography.Link>
                  ) : (
                    <Typography.Text>{serviceName}</Typography.Text>
                  )}
                  <Typography.Text>
                    <Typography.Text strong>{count}</Typography.Text>
                    <Typography.Text type="secondary"> ({percent}%)</Typography.Text>
                  </Typography.Text>
                </Flex>
                <Progress
                  percent={percent}
                  strokeColor={color}
                  showInfo={false}
                  size="small"
                />
              </Flex>
            );
          })}

          {totalPages > 1 && (
            <Flex justify="center" align="center" gap="small">
              <Button
                size="small"
                icon={<LeftOutlined />}
                disabled={currentPage === 0}
                onClick={() => setCurrentPage((page) => page - 1)}
              />
              <Typography.Text type="secondary">
                {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, entries.length)} /{" "}
                {entries.length}
              </Typography.Text>
              <Button
                size="small"
                icon={<RightOutlined />}
                disabled={currentPage + 1 >= totalPages}
                onClick={() => setCurrentPage((page) => page + 1)}
              />
            </Flex>
          )}
        </Flex>
      )}
    </Card>
  );
};
